import { createTheme } from '@mui/material/styles';
import {
    blue,
    pumpkin,
    purple,
    orange,
    teal,
    olive,
    skyBlue,
    lavender,
    pointlessPuple,
    yellow,
    red,
    pointlessPink,
    normal,
    warning,
    critical,
    normalLight,
    warningLight,
    criticalLight,
    neutrals1,
    neutrals2,
} from './colors';

const LightTheme = createTheme({
    palette: {
        mode: 'light',
        primary: {
            main: blue[600],
            light: blue[700],
            dark: blue[500],
            label: blue[700], // primary.dark for this theme
            contrastText: '#FFF',
        },
        secondary: {
            main: pumpkin[400],
            light: pumpkin[600],
            dark: pumpkin[200],
            contrastText: '#FFF',
        },
        tertiary: {
            main: neutrals2[800],
        },
        background: {
            default: neutrals1[0], // main background
            selection: neutrals2[100], // filter op node background
            paper: 'white', // sensor card, search box,
            selectionAlt: blue[100], // variable node background in filter
            textAlt: neutrals2[500], // variable node drag icon in filter
            read: neutrals2[50],
        },
        nav: {
            nested: blue[900], // nav nested items
            selection: blue[600], // nav selected items
            topNav: blue[900], // top nav
            paperAlt: blue[900], // side nav
            primary: blue[400], // buttons on top nav which is always black
            banner: blue[800], // header and footer
            paper: blue[800], // top nav background
            text: 'rgba(255, 255, 255, 0.87)', // sidebar text
            textDrawer: '#000000',
        },
        success: {
            main: normalLight,
            dark: normalLight,
            light: normalLight,
            contrastText: '#FFF',
        },
        warning: {
            main: warningLight,
            dark: warningLight,
            light: warningLight,
            color: 'rgba(0, 0, 0, 0.87)',
        },
        error: {
            main: criticalLight,
            dark: criticalLight,
            light: criticalLight,
            contrastText: '#FFF',
        },
        info: {
            main: teal[800],
            light: teal[900],
            dark: teal[700],
            contrastText: '#FFF',
        },
        backtesting: {
            main: purple[300],
            light: purple[25],
            dark: purple[300],
            table: purple[25],
            contrastText: '#FFF',
            read: purple[10],
        },
    },
    typography: {
        fontFamily: '"Noto Sans", "Helvetica", "Arial", sans-serif',
        tooltips: {
            font: '11px sans-serif',
        },
    },
    transitions: {
        duration: {
            missles: 1000,
            newTrafficShort: 8000,
            newTrafficLong: 8000,
            rippleSpeed: 800,
            rippleFade: 2000,
            complex: 420,
        }
    },
    // these are moment.js format strings
    date: {
        longtz: 'HH:mm:ss MM/DD/YY ZZ',
        long: 'HH:mm:ss MM/DD/YY',
        short: 'HH:mm MM/DD/YY',
    },
    styled: {
        paper: {
            background: '#FFF',
            border: blue[600],
        },
        slider: {
            overlay: neutrals2[400],
            window: neutrals2[300],
            guage: neutrals2[100],
        },
        filterbuilder: {
            opBackground: neutrals2[700],
            opButton: blue[100],
            opHandle: blue[100],
            opAction: blue[100],
            varBackground: neutrals1[200],
            varHandle: blue[100],
            varValue: '#fff',
            varAction: blue[100],
        },
    },
    SeverityColors: [criticalLight, warningLight, normalLight, skyBlue[400]],
    GeoNodeColors: [orange[400], pumpkin[400], yellow[400]],
    ChartColors: [
        // teal[600],
        blue[600],
        lavender[400],
        skyBlue[400],
        pumpkin[400],
        teal[600],
        purple[400],
        orange[400],
        blue[400],
        olive[400],
        pointlessPuple[400],
        yellow[400],
        red[400],
        pointlessPink[400],
        teal[200],
        lavender[200],
        skyBlue[200],
        pumpkin[200],
        purple[200],
        orange[200],
        blue[200],
        olive[200],
        pointlessPuple[200],
        pointlessPink[200],
    ],
    HeatmapColors: [
        // critical, //red[400],
        // pumpkin[300],
        // warning, // yellow[550],
        // neutrals1[100],
        criticalLight,
        warningLight,
        normalLight,
        skyBlue[400]
    ],
    // https://github.com/chriskempson/base16/blob/master/styling.md
    //  In order to create a dark theme, colors base00 to base07 should span from dark to light.
    // For a light theme, these colours should span from light to dark.
    JsonViewerTheme: {
        // ypically variations of a shade and run from darkest to lightest.
        // These colors are used for foreground and background, status bars, line highlighting and such
        base00: 'white', // Default Background
        base01: blue[100], // Lighter Background (Used for status bars)
        base02: blue[100], // Selection Background
        base03: blue[600], // Comments, Invisibles, Line Highlighting
        base04: blue[700], // Dark Foreground (Used for status bars)
        base05: blue[800], // Default Foreground, Caret, Delimiters, Operators
        base06: blue[900], // Light Foreground (Not often used)
        base07: blue[1100], // Light Background (Not often used)

        // typically individual colors used for types, operators, names and variables
        base08: teal[600], // Variables, XML Tags, Markup Link Text, Markup Lists, Diff Deleted
        base09: purple[500], // Integers, Boolean, Constants, XML Attributes, Markup Link Url
        base0A: teal[600], // Classes, Markup Bold, Search Text Background
        base0B: pumpkin[400], // Strings, Inherited Class, Markup Code, Diff Inserted
        base0C: blue[600], // Support, Regular Expressions, Escape Characters, Markup Quotes
        base0D: orange[600], // Functions, Methods, Attribute IDs, Headings
        base0E: olive[600], // Keywords, Storage, Selector, Markup Italic, Diff Changed
        base0F: skyBlue[500], // Deprecated, Opening/Closing Embedded Language Tags, e.g. <?php ?>
    },
    codeMirror: {
        theme: 'xq-light',
    },
    // I shouldn't have to do this
    components: {
        MuiTooltip: {
            styleOverrides: {
                tooltip: {
                    color: 'rgba(0, 0, 0, 0.87)',
                }
            }
        },
        MuiTab: {
            styleOverrides: {
                textColorPrimary: {
                    '&.Mui-selected': {
                        color: 'rgba(255, 255, 255, 0.95)',
                    },
                    color: 'rgba(255, 255, 255, 0.5)',
                },
            }
        }
    },
});

const DarkTheme = createTheme({
    palette: {
        mode: 'dark',
        primary: {
            main: blue[400],
            dark: blue[600],
            light: blue[200],
            contrastText: '#FFF',
            label: blue[200], // primary.light for this theme
        },
        secondary: {
            main: pumpkin[400],
            dark: pumpkin[600],
            light: pumpkin[200],
            contrastText: '#FFF',
        },
        tertiary: {
            main: olive[300],
        },
        background: {
            default: blue[1100], // main background
            selection: blue[700],
            paper: blue[900], // sensor card, search box,
            selectionAlt: neutrals1[300], // variable node background in filter
            textAlt: neutrals2[500], // variable node drag icon in filter
            read: blue[1000],
        },
        nav: {
            nested: blue[1000], // nav nested items
            selection: blue[700], // nav selected items
            topNav: '#000000', // top nav
            paperAlt: blue[900], // side nav
            primary: blue[400], // buttons on top nav which is always black
            banner: blue[1100], // header and footer
            paper: '#000000', // top nav background
            text: 'rgba(255, 255, 255, 0.87)', // sidebar text
            textDrawer: '#FFFFFF',
        },
        success: {
            main: normal,
            dark: normal,
            light: normal,
            contrastText: 'rgba(0, 0, 0, 0.87)',
        },
        warning: {
            main: yellow[400],
            dark: yellow[600],
            light: yellow[400],
            contrastText: 'rgba(0, 0, 0, 0.87)',
        },
        error: {
            main: critical,
            dark: critical,
            light: critical,
            contrastText: '#FFF',
        },
        info: {
            main: teal[400],
            dark: teal[600],
            light: teal[400],
            contrastText: 'rgba(0, 0, 0, 0.87)',
        },
        backtesting: {
            main: purple[300],
            light: purple[25],
            dark: purple[400],
            table: purple[900],
            contrastText: '#FFF',
            read: purple[950],
        },
    },
    typography: {
        fontFamily: '"Noto Sans", "Helvetica", "Arial", sans-serif',
        tooltips: {
            font: '11px sans-serif',
        },
    },
    transitions: {
        duration: {
            missles: 1000,
            newTrafficShort: 8000,
            newTrafficLong: 8000,
            rippleSpeed: 800,
            rippleFade: 2000,
            complex: 420,
        }
    },
    // these are moment.js format strings
    date: {
        longtz: 'HH:mm:ss MM/DD/YY ZZ',
        long: 'HH:mm:ss MM/DD/YY',
        short: 'HH:mm MM/DD/YY',
    },
    styled: {
        paper: {
            background: '#000000',
            border: blue[850],
        },
        slider: {
            overlay: neutrals2[400],
            window: neutrals2[300],
            guage: neutrals2[600],
        },
        filterbuilder: {
            opBackground: blue[700],
            opButton: blue[400],
            opHandle: blue[400],
            opAction: '#fff',
            varBackground: neutrals1[300],
            varHandle: neutrals2[500],
            varValue: '#fff',
            varAction: '#fff',
        }
    },
    SeverityColors: [critical, warning, normal, skyBlue[400]],
    GeoNodeColors: [orange[400], pumpkin[400], yellow[400]],
    ChartColors: [
        teal[400],
        lavender[400],
        skyBlue[400],
        pumpkin[400],
        purple[400],
        orange[400],
        blue[400],
        olive[400],
        pointlessPuple[400],
        yellow[400],
        red[400],
        pointlessPink[400],
        teal[200],
        lavender[200],
        skyBlue[200],
        pumpkin[200],
        purple[200],
        orange[200],
        blue[200],
        olive[200],
        pointlessPuple[200],
        pointlessPink[200],
    ],
    HeatmapColors: [
        // critical, //red[400],
        // pumpkin[300],
        // warning, // yellow[550],
        // neutrals1[100],
        criticalLight,
        warningLight,
        normalLight,
        skyBlue[400]
    ],

    // https://github.com/chriskempson/base16/blob/master/styling.md
    JsonViewerTheme: {
        base00: blue[1100], // Default Background
        base01: blue[1000], // Lighter Background (Used for status bars)
        base02: blue[900], // Selection Background
        base03: blue[800], // Comments, Invisibles, Line Highlighting
        base04: blue[400], // Dark Foreground (Used for status bars)
        base05: blue[300], // Default Foreground, Caret, Delimiters, Operators
        base06: blue[200], // Light Foreground (Not often used)
        base07: blue[100], // Light Background (Not often used)

        base08: teal[400], // Variables, XML Tags, Markup Link Text, Markup Lists, Diff Deleted
        base09: blue[400], // Integers, Boolean, Constants, XML Attributes, Markup Link Url
        base0A: teal[400], // Classes, Markup Bold, Search Text Background
        base0B: pumpkin[400], // Strings, Inherited Class, Markup Code, Diff Inserted
        base0C: purple[400], // Support, Regular Expressions, Escape Characters, Markup Quotes
        base0D: orange[400], // Functions, Methods, Attribute IDs, Headings
        base0E: olive[400], // Keywords, Storage, Selector, Markup Italic, Diff Changed
        base0F: skyBlue[400], // Deprecated, Opening/Closing Embedded Language Tags, e.g. <?php ?>
    },
    codeMirror: {
        theme: 'material',
    },
    components: {
        // MuiTooltip: {
        //     styleOverrides: {
        //         tooltip: {
        //             color: 'rgba(255, 255, 255, 0.87)',
        //         },
        //     }
        // },
        MuiButton: {
            styleOverrides: {
                containedInherit: {
                    color: 'rgba(0, 0, 0, 0.87)'
                }
            }
        }
    }
});

const PinkTheme = createTheme({
    palette: {
        mode: 'dark',
        primary: {
            main: pointlessPuple[400],
            dark: pointlessPuple[600],
            light: pointlessPuple[200],
            contrastText: '#FFF',
            label: pointlessPuple[200], // primary.light for this theme
        },
        secondary: {
            main: lavender[400],
            dark: lavender[600],
            light: lavender[200],
            contrastText: '#FFF',
        },
        tertiary: {
            main: olive[300],
        },
        background: {
            default: pointlessPink[1100], // main background
            selection: pointlessPink[700],
            paper: pointlessPink[900], // sensor card, search box,
            selectionAlt: neutrals1[300], // variable node background in filter
            textAlt: neutrals2[500], // variable node drag icon in filter
            read: pointlessPink[1000],
        },
        nav: {
            nested: pointlessPink[1000], // nav nested items
            selection: pointlessPink[700], // nav selected items
            topNav: pointlessPink[900],
            paperAlt: pointlessPink[900], // side nav
            primary: pointlessPink[400], // buttons on top nav which is always black
            banner: pointlessPink[1100], // header and footer
            paper: '#000000', // top nav background
            text: 'rgba(255, 255, 255, 0.87)', // sidebar text
            textDrawer: '#FFFFFF',
        },
        success: {
            main: normal,
            dark: normal,
            light: normal,
            contrastText: 'rgba(0, 0, 0, 0.87)',
        },
        warning: {
            main: yellow[400],
            dark: yellow[600],
            light: yellow[400],
            contrastText: 'rgba(0, 0, 0, 0.87)',
        },
        error: {
            main: critical,
            dark: critical,
            light: critical,
            contrastText: '#FFF',
        },
        info: {
            main: teal[400],
            dark: teal[600],
            light: teal[400],
            contrastText: 'rgba(0, 0, 0, 0.87)',
        },
        backtesting: {
            main: purple[300],
            light: purple[25],
            dark: purple[400],
            table: purple[900],
            contrastText: '#FFF',
            read: purple[950],
        },
    },
    typography: {
        fontFamily: '"Noto Sans", "Helvetica", "Arial", sans-serif',
        tooltips: {
            font: '11px sans-serif',
        },
    },
    transitions: {
        duration: {
            missles: 1000,
            newTrafficShort: 8000,
            newTrafficLong: 8000,
            rippleSpeed: 800,
            rippleFade: 2000,
            complex: 420,
        }
    },
    // these are moment.js format strings
    date: {
        longtz: 'HH:mm:ss MM/DD/YY ZZ',
        long: 'HH:mm:ss MM/DD/YY',
        short: 'HH:mm MM/DD/YY',
    },
    styled: {
        paper: {
            background: '#000000',
            border: blue[850],
        },
        slider: {
            overlay: neutrals2[400],
            window: neutrals2[300],
            guage: neutrals2[600],
        },
        filterbuilder: {
            opBackground: blue[700],
            opButton: blue[400],
            opHandle: blue[400],
            opAction: '#fff',
            varBackground: neutrals1[300],
            varHandle: neutrals2[500],
            varValue: '#fff',
            varAction: '#fff',
        }
    },
    SeverityColors: [critical, warning, normal, skyBlue[400]],
    GeoNodeColors: [orange[400], pumpkin[400], yellow[400]],
    ChartColors: [
        teal[400],
        lavender[400],
        skyBlue[400],
        pumpkin[400],
        purple[400],
        orange[400],
        blue[400],
        olive[400],
        pointlessPuple[400],
        yellow[400],
        red[400],
        pointlessPink[400],
        teal[200],
        lavender[200],
        skyBlue[200],
        pumpkin[200],
        purple[200],
        orange[200],
        blue[200],
        olive[200],
        pointlessPuple[200],
        pointlessPink[200],
    ],
    HeatmapColors: [
        // critical, //red[400],
        // pumpkin[300],
        // warning, // yellow[550],
        // neutrals1[100],
        criticalLight,
        warningLight,
        normalLight,
        skyBlue[400]
    ],
    // https://github.com/chriskempson/base16/blob/master/styling.md
    JsonViewerTheme: {
        base00: blue[1100], // Default Background
        base01: blue[1000], // Lighter Background (Used for status bars)
        base02: blue[900], // Selection Background
        base03: blue[800], // Comments, Invisibles, Line Highlighting
        base04: blue[400], // Dark Foreground (Used for status bars)
        base05: blue[300], // Default Foreground, Caret, Delimiters, Operators
        base06: blue[200], // Light Foreground (Not often used)
        base07: blue[100], // Light Background (Not often used)

        base08: teal[400], // Variables, XML Tags, Markup Link Text, Markup Lists, Diff Deleted
        base09: blue[400], // Integers, Boolean, Constants, XML Attributes, Markup Link Url
        base0A: teal[400], // Classes, Markup Bold, Search Text Background
        base0B: pumpkin[400], // Strings, Inherited Class, Markup Code, Diff Inserted
        base0C: purple[400], // Support, Regular Expressions, Escape Characters, Markup Quotes
        base0D: orange[400], // Functions, Methods, Attribute IDs, Headings
        base0E: olive[400], // Keywords, Storage, Selector, Markup Italic, Diff Changed
        base0F: skyBlue[400], // Deprecated, Opening/Closing Embedded Language Tags, e.g. <?php ?>
    },
    codeMirror: {
        theme: 'material',
    },
    components: {
        // MuiTooltip: {
        //     styleOverrides: {
        //         tooltip: {
        //             color: 'rgba(255, 255, 255, 0.87)',
        //         },
        //     }
        // },
        MuiButton: {
            styleOverrides: {
                containedInherit: {
                    color: 'rgba(0, 0, 0, 0.87)'
                }
            }
        }
    }
});

// convert theme.spacing to int for spacing math (e.g. '8px' => 8)
const getSpacing = (theme, n) => parseInt(theme.spacing(n), 10);
PinkTheme.getSpacing = n => getSpacing(LightTheme, n);
DarkTheme.getSpacing = n => getSpacing(LightTheme, n);
LightTheme.getSpacing = n => getSpacing(LightTheme, n);

export default {
    DarkTheme,
    LightTheme,
    PinkTheme,
};
